import { useContext, useEffect, useRef, useState } from 'react';
import {
	ElementsProps,
	FormRiskStep,
	JobRiskFactorsProps,
	StepProps,
} from './FormRisk.types';
import {
	getJobProfileById,
	getJobProfileRisksById,
	putJobProfileRisksById,
} from 'Views/JobProfile/services/services';
import { JobProfileContext } from 'Views/JobProfile/JobProfile.context';
import { loggerInfo } from 'utils/infoLog';
import { sanitizeWithNull } from 'helpers/basicFunctions';
import { useNavigate } from 'react-router';
import { useToast } from '@welbe/web-design-system';

const order = [
	'Físicos',
	'Químicos',
	'Biológicos',
	'Ergonómicos',
	'Psicosociales',
];

export const useFormRisk = () => {
	const { toast } = useToast();
	const {
		id,
		jobProfileType,
		riskId,
		riskIndex,
		focusedId,
		isLoading,
		jobRiskFactorsById,
		jobRiskFactors,
		active,
		setActive,
		setJobRiskFactors,
		setLoading,
		setFocusedId,
		setJobRiskFactorsById,
		jobRiskFactorsError,
		setJobRiskFactorsError,
	} = useContext(JobProfileContext);
	const [steps, setSteps] = useState<StepProps[]>([]);

	const navigate = useNavigate();
	const preventRerender = useRef(false);

	const [openFinishModal, setOpenFinishModal] = useState(false);
	const [complete, setComplete] = useState(0);
	const [formLoading, setFormLoading] = useState(false);
	const [isProfileError, setProfileError] = useState(false);
	const [stepperLoading, setStepperLoading] = useState(false);
	const [riskFormsTypes, setRiskFormsTypes] = useState<ElementsProps[]>([]);
	const stepsMaxSize = steps.length - 1;
	const stepsMinSize = 0;
	const isLastForm = active === stepsMaxSize;
	const isFirstForm = active === stepsMinSize;

	const handleNavigate = (item: any) => {
		setActive(item.id);
	};

	const getPayloadFormatted = () => {
		const payload = jobRiskFactors
			.filter((item) => item.isChecked)
			.map((item: any) => ({
				id: item.jobProfileRiskFactor.id,
				isChecked: item.isChecked,
				exposureLevel: item.jobProfileRiskFactor.exposureLevel,
				examResult: item.jobProfileRiskFactor.examResult,
				frequency: item.jobProfileRiskFactor.frequency,
				actualConditions: item.jobProfileRiskFactor.actualConditions,
				recommendedActions: item.jobProfileRiskFactor.recommendedActions,
				hasFile: Boolean(item.jobProfileRiskFactor.hasFile),
				exposure: item.jobProfileRiskFactor?.exposure?.filter(
					(exposure: string) => exposure !== '',
				),
				jobExamProfileRiskFactors:
					item.jobProfileRiskFactor.jobExamProfileRiskFactors.map(
						(jobProfile: any) => ({
							days: jobProfile.days ? Number(jobProfile.days) : null,
							frequency: jobProfile.frequency,
							type: jobProfile.type,
							examId: jobProfile.exam.id,
						}),
					),
			}));

		return {
			id: id,
			jobRiskFactors: payload,
			riskId: jobRiskFactorsById.riskId,
		};
	};

	const handleValidateForm = (): boolean => {
		let isValid = true;
		jobRiskFactors
			.filter((item) => item.isChecked)
			.map((item: any) => {
				item.jobProfileRiskFactor.jobExamProfileRiskFactors.map(
					(jobProfile: any) => {
						if (!jobProfile.frequency || !jobProfile.type.length) {
							isValid = false;
							const indexFactor = jobRiskFactorsError.findIndex(
								(factor) => factor.id === item.jobProfileRiskFactor.id,
							);
							const indexExam = jobRiskFactorsError[
								indexFactor
							].jobExamProfileRiskFactors.findIndex(
								(exam) => exam.examId === jobProfile.exam.id,
							);
							jobRiskFactorsError[indexFactor].jobExamProfileRiskFactors[
								indexExam
							].frequency = !jobProfile?.frequency
								? 'Este campo es obligatorio'
								: '';
							jobRiskFactorsError[indexFactor].jobExamProfileRiskFactors[
								indexExam
							].type = !jobProfile?.type?.length
								? 'Este campo es obligatorio'
								: '';
							setJobRiskFactorsError([...jobRiskFactorsError]);
						}
					},
				);
			});
		return isValid;
	};

	const handleNext = async () => {
		try {
			if (handleValidateForm()) {
				setLoading(true);
				const payload = getPayloadFormatted();
				await putJobProfileRisksById(id, sanitizeWithNull(payload));
				setJobRiskFactorsById({
					id: '',
					riskId: '',
					jobRiskFactors: [],
				});

				if (!isLastForm) {
					setActive(active + 1);
					toast.success('Riesgo actualizado');
					return;
				}
				setOpenFinishModal(true);
			}
		} catch (error) {
			toast.error('Error al actualizar el riesgo');
		} finally {
			setLoading(false);
		}
	};

	const handlePrev = () => {
		if (isFirstForm) {
			setJobRiskFactors([]);
			setJobRiskFactorsById({
				id: '',
				riskId: '',
				jobRiskFactors: [],
			});
			if (riskId) {
				navigate(`/job-profile/summary/${jobProfileType}/${id}`);
				return;
			}
			navigate('/job-profile');
			return;
		}
		setActive(active - 1);
	};

	const resetError = () => {
		setProfileError(false);
	};

	const FormatRiskStep = (
		riskStep: FormRiskStep,
		index: number,
	): StepProps => ({
		name: riskStep.name,
		itemId: riskStep.id,
		id: index,
	});

	const FormatData = (riskId: string, data: any[]) => {
		const jobRiskFactors = data.map((item) => ({
			id: item.jobProfileRiskFactor.id,
			exposureLevel: item.jobProfileRiskFactor.exposureLevel,
			exposure: item.jobProfileRiskFactor.exposure,
			examResult: item.jobProfileRiskFactor.examResult,
			frequency: item.jobProfileRiskFactor.frequency,
			actualConditions: item.jobProfileRiskFactor.actualConditions,
			jobExamProfileRiskFactors:
				item.jobProfileRiskFactor.jobExamProfileRiskFactors.map(
					(jobProfile: any) => ({
						days: jobProfile.days,
						frequency: jobProfile.frequency,
						type: jobProfile.type,
						examId: jobProfile.exam.id,
					}),
				),
		}));

		const jobRiskFactorsError = data.map((item) => ({
			id: item.jobProfileRiskFactor.id,
			examResult: '',
			examFileName: '',
			exposure: '',
			exposureLevel: '',
			frequency: '',
			actualConditions: '',
			recommendedActions: '',
			jobExamProfileRiskFactors:
				item.jobProfileRiskFactor.jobExamProfileRiskFactors.map(
					(jobProfile: any) => ({
						examId: jobProfile.exam.id,
						days: '',
						frequency: '',
						type: '',
					}),
				),
		}));

		setJobRiskFactorsError(jobRiskFactorsError);
		setJobRiskFactorsById((prev: any) => ({
			...prev,
			jobRiskFactors,
			riskId,
		}));
		setFocusedId(jobRiskFactors[0].id);
	};

	const fetchJobProfile = async () => {
		try {
			resetError();
			setStepperLoading(true);
			setFormLoading(true);
			const { data } = await getJobProfileById(id);
			const riskSteps = data.content.risks
				.sort((a: any, b: any) => order.indexOf(a.name) - order.indexOf(b.name))
				.map(FormatRiskStep);

			if (riskId) {
				setSteps(riskSteps.filter((item: any) => item.itemId === riskId));
				return;
			}
			setSteps(riskSteps);
		} catch (error) {
			setProfileError(true);
			toast.error('Error al cargar lo perfil');
			loggerInfo({
				data: error,
				context: 'job-profile',
				location: 'fetchJobProfile',
				payloadRequest: { id },
			});
		} finally {
			setStepperLoading(false);
		}
	};

	const fetchJobProfileRisksById = async (riskSteps: StepProps[]) => {
		const riskId = riskSteps[active]?.itemId;

		if (!riskId) return;

		try {
			setFormLoading(true);
			const { data } = await getJobProfileRisksById(id, riskId);
			const risks = data.content;
			const hasCheckedItem = !!risks?.jobRiskFactors.find(
				(item: JobRiskFactorsProps) => item.isChecked,
			);
			if (!hasCheckedItem) {
				risks.jobRiskFactors[0].isChecked = true;
			}
			setJobRiskFactors(risks.jobRiskFactors);
			setRiskFormsTypes(risks.optionType);

			FormatData(riskId, risks.jobRiskFactors);
		} catch (error) {
			setProfileError(true);
			toast.error('Error al cargar los riesgos');
			loggerInfo({
				data: error,
				context: 'job-profile',
				location: 'fetchJobProfileRisksById',
				payloadRequest: { id, riskId },
			});
		} finally {
			setFormLoading(false);
		}
	};

	const closeFinishModal = () => {
		navigate(`/job-profile/summary/${jobProfileType}/${id}`);
	};

	useEffect(() => {
		if (steps.length === 0) return;
		fetchJobProfileRisksById(steps);

		if (active > complete) {
			setComplete(active);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [active, steps]);

	useEffect(() => {
		if (!preventRerender.current) {
			preventRerender.current = true;
			fetchJobProfile();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		steps,
		active,
		complete,
		focusedId,
		stepperLoading,
		formLoading,
		isLastForm,
		riskFormsTypes,
		jobRiskFactors,
		isProfileError,
		riskIndex,
		isLoading,
		openFinishModal,
		closeFinishModal,
		handleRefresh: fetchJobProfile,
		handlePrev,
		handleNext,
		handleNavigate,
	};
};
